<template>
  <footer
    itemscope
    itemtype="https://schema.org/Organization"
    :class="{ 'has-sidebar': isSidebarVisible }"
    class="footer">
    <div class="footer__container container">
      <div
        class="footer__body"
        :class="{'footer__body--expanded': isFooterExpanded}">
        <div class="footer__main">
          <button
            v-if="!isPlatformApp"
            class="button button-main"
            @click="openIntercom">
            <img
              src="/images/icons/chat2.svg"
              alt=""
              width="15"
              height="15"
              class="button-icon">
            <span>LiveHelp — Chat with us</span>
          </button>

          <div class="footer__contact-links">
            <span
              class="visually-hidden"
              itemprop="name">
              Skycoach
            </span>
            <router-link
              :to="{ name: 'root' }"
              class="footer__logo">
              <img
                itemprop="logo"
                src="/images/logos/skycoach.svg"
                alt="Skycoach logo"
                class="footer__logo-img"
                width="134"
                height="32">
            </router-link>
            <a
              href="tel:+18554011156"
              class="contact-links-item phone">
              +1 855 401 11 56
            </a>
            <span
              class="visually-hidden"
              itemprop="telephone">+1 (855) 401 11 56</span>
            <lazy-hydrate never>
              <a
                href="mailto:support@skycoach.gg"
                class="contact-links-item email">
                support@skycoach.gg
              </a>
            </lazy-hydrate>
            <span
              class="visually-hidden"
              itemprop="email">support@skycoach.gg</span>
            <lazy-hydrate never>
              <a
                href="mailto:copyright@skycoach.gg"
                class="contact-links-item email">
                copyright@skycoach.gg
              </a>
            </lazy-hydrate>
            <p
              class="visually-hidden"
              itemprop="slogan">
              We try hard
            </p>
          </div>

          <div class="footer__social">
            <div class="footer__join-us category__title-text">
              Join us
            </div>
            <div class="footer__social-wrapper">
              <facebook-icon class="social-item" />
              <instagram-icon class="social-item" />
            </div>
          </div>

          <template
            v-if="!isPlatformApp">
            <customer-category-links
              v-for="(category) in links"
              :key="category.title"
              :category="category"
              :should-toggle-collapse="isMobile"
              :class="[
                'links__category',
                { 'category-sitemap': category.title === 'Sitemap' },
                { 'category-company': category.title === 'Company' },
                { 'category-customer': category.title === 'Customer Service' },
                { 'category-legal-and-policies': category.title === 'Legal & Policies' },
              ]">
              <template #title="{ isOpened }">
                <div class="category__title">
                  <router-link
                    v-slot="{ href, navigate }"
                    :to="category.titleLink || ''"
                    custom
                    class="category__title-text"
                    :class="{ 'category__title-text--link': category.titleLink || false }">
                    <component
                      :is="category.titleLink ? 'a' : 'span'"
                      :href="href"
                      @click="category.titleLink ? navigate : void(0)">
                      {{ category.title }}
                    </component>
                  </router-link>

                  <div
                    :class="{ 'category__title-shape--active': isOpened }"
                    class="category__title-shape" />
                </div>
              </template>

              <template #links="{ links, more }">
                <div class="category__links-list">
                  <component
                    :is="link.isExternalLink ? 'a' : 'router-link'"
                    v-for="link in links"
                    v-show="!link._isVisuallyHidden"
                    :key="`${link._href}-${link.title}`"
                    :to="link._href"
                    :href="link._href"
                    :rel="link.isNoFollow ? 'nofollow' : ''"
                    :target="link.isExternalLink ? '_blank' : category.target"
                    class="category__link transition--color"
                    @click.native="$emit('linkClick')">
                    {{ link.title_footer ? link.title_footer : link.title }}
                  </component>

                  <router-link
                    v-if="(more && more.amount) || category._visuallyHiddenLinksCount > 0"
                    :to="more.link"
                    class="category__link-more transition--color-bg">
                    {{ category._visuallyHiddenLinksCount + (more.amount || 0) }} more
                  </router-link>
                </div>
              </template>
            </customer-category-links>
          </template>
        </div>

        <button
          v-if="!isFooterExpanded && !isPlatformApp"
          class="button button-dark-additional footer__show-more-button"
          @click="isFooterExpanded = true">
          Load more games

          <img
            class="footer__show-more-button-arrow"
            src="/images/icons/arrow-simple.svg"
            alt="Show more"
            width="24"
            height="24">
        </button>
      </div>

      <div class="footer__bottom">
        <div class="bottom__content-line">
          <div class="bottom__payment-systems">
            <img
              v-for="item in paymentMethods"
              :key="item.id"
              alt=""
              width="46"
              height="17"
              class="payment-item"
              :src="`/images/payment_systems/white/${item.pic}`">
          </div>
          <div
            v-if="!isPlatformApp"
            class="bottom__app-markets">
            <appstore-button
              class="bottom__appstore"
              type="footer" />
            <googleplay-button
              class="bottom__googleplay"
              type="footer" />
          </div>
        </div>
        <div class="bottom_text">
          © 2020 — {{ (new Date().getFullYear()) }} All rights reserved <br>
          <span itemprop="legalName">DEVAL LIMITED </span>
          <span itemprop="address">25 Martiou, 27 D. Michael Tower, flat/office 105A, Egkomi, 2408 </span>
          <span itemprop="location">Nicosia, Cyprus </span>
          Reg.No. ΗΕ 432317
        </div>
        <p class="bottom_text">
          The Platform is not endorsed by, directly affiliated with, maintained, or sponsored by Blizzard Entertainment,
          Bungie, Electronic Arts, Grinding Gear Games, Activision Publishing, Square Enix Co., Valve, Battlestate
          Games, Wargaming.net Limited, Amazon Technologies, Jagex Limited, Riot Games, Smilegate RPG, Digital Extremes,
          Roblox Corporation. The views and opinions expressed by the Platform do not reflect those of anyone officially
          associated with producing or managing their game franchises. Copyrighted art submitted to or through the
          Platform remains the intellectual property of the respective copyright holder.
        </p>
      </div>
    </div>
  </footer>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import GameModel from 'Models/GameModel';
import { scrollTo } from 'Root/helpers/tools';
import FacebookIcon from 'Components/Socials/Facebook.vue';
import InstagramIcon from 'Components/Socials/Instagram.vue';
import PaymentSystemModel from 'Models/PaymentSystemModel';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import LazyHydrate from 'vue-lazy-hydration';
import { StaticLinksCompany, StaticLinksCustomerService, StaticLinksLegal } from 'Models/StaticLinksModel';
import CustomerCategoryLinks from 'Components/CustomerCategoryLinks.vue';
import AppstoreButton from 'Components/ui/AppMarkets/AppstoreButton.vue';
import GoogleplayButton from 'Components/ui/AppMarkets/GoogleplayButton.vue';
import { flatTagsById } from 'Root/helpers/tags';

export default {
  name: 'CustomerFooter',

  components: {
    GoogleplayButton,
    AppstoreButton,
    FacebookIcon,
    InstagramIcon,
    LazyHydrate,
    CustomerCategoryLinks,
  },

  props: {
    isSidebarVisible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      scrollTo,
      links: [],
      flatTagsById,
      isFooterExpanded: false,
    };
  },

  computed: {
    ...mapState('products', ['tags']),
    ...mapState(['userDevice', 'activeRegion', 'user']),
    ...mapGetters(['isPlatformApp']),

    paymentMethods() {
      return PaymentSystemModel.METHODS_LIST
        .filter((method) => method.id !== PaymentSystemModel.PAYMENTWALL.id)
        .filter((method, index, list) => list
          .findIndex((item) => (item.title === method.title)) === index);
    },

    isMobile() {
      return this.userDevice.innerWidth <= 1024;
    },
  },

  created() {
    if (!this.tags?.length) {
      this.$store.dispatch('products/getData').then(() => {
        this.setLinks();
      });
      return;
    }

    this.setLinks();
  },

  methods: {
    getGameLinks(seoTagsByGame) {
      const gameLinks = [];
      let titleLink;

      if (Object.keys(flatTagsById).length) {
        GameModel.LIST_ACTIVE.sort((a, b) => b.defaultPriority - a.defaultPriority).forEach(({
          _linkToTagId, routeNamespace, id, menuTitle, fullTitle,
        }) => {
          titleLink = _linkToTagId
            ? flatTagsById[_linkToTagId]?._href
            : `/${routeNamespace}-boost`;

          if (seoTagsByGame[id]) {
            let visuallyHiddenLinksCount = 0;
            const links = seoTagsByGame[id].visible.map((tag, index) => {
              if (index > 2) {
                visuallyHiddenLinksCount += 1;
              }

              return {
                ...tag,
                _isVisuallyHidden: index > 2,
              };
            });

            gameLinks.push({
              title: menuTitle || fullTitle,
              titleLink,
              more: { amount: seoTagsByGame[id].more, link: `/${routeNamespace}-boost` },
              links,
              _visuallyHiddenLinksCount: visuallyHiddenLinksCount,
            });
          }
        });
      }

      return gameLinks;
    },

    setLinks() {
      const seoTagsByGame = this.getSeoTags();
      const gameLinks = this.getGameLinks(seoTagsByGame);
      this.links = [StaticLinksCompany, StaticLinksCustomerService, StaticLinksLegal, ...gameLinks];
    },

    getSeoTags() {
      const seoTagsByGame = {};

      this.tags.forEach((tag) => {
        if (seoTagsByGame[tag.game_id] === undefined) {
          seoTagsByGame[tag.game_id] = {
            visible: [],
            more: 0,
          };
        }

        if (tag.is_seo) {
          seoTagsByGame[tag.game_id].visible.push(tag);
        } else if (!tag.parent_id) {
          seoTagsByGame[tag.game_id].more += 1;
        }
      });

      return seoTagsByGame;
    },

    openIntercom() {
      googleTagAnalytics.emitItemOpenChatEvent();
      IntercomApiDriver.show();
    },
  },
};
</script>

<style lang="scss" scoped>
  .footer__container {
    display: grid;
  }

  .footer__body {
    position: relative;
    overflow: hidden;
    max-height: 730px;
    transition: max-height 0.3s ease;

    &:after {
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 132px;
      background: linear-gradient(0deg, #213041 0%, rgba(33, 48, 65, 0) 100%);
      transition: height, opacity 0.3s ease;
    }
  }

  .footer__body--expanded {
    max-height: 100% !important;

    &:after {
      display: none;
      height: 0;
      opacity: 0;
    }
  }

  .footer__show-more-button {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    display: flex;
    align-items: center;
    padding: 12px;
    font-size: 15px;
  }

  .footer__show-more-button-arrow {
    margin-left: 12px;
    width: 24px;
    height: 24px;
    transform: rotate(90deg);
  }

  .footer__main {
    display: grid;
    grid-template-rows: auto;
    grid-template-areas:
          "chat-button chat-button"
          "contact-links socials";
    line-height: 1.2;
    margin-top: 24px;
  }

  .footer__logo {
    display: none;
  }

  .button-main {
    grid-area: chat-button;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    font-size: 13px;
    margin-bottom: 24px;
    min-width: 185px;

    .button-icon {
      margin-right: 8px;
    }
  }

  .footer__contact-links {
    grid-area: contact-links;
    display: flex;
    flex-direction: column;
    font-size: 13px;
    margin-bottom: 24px;
  }

  .contact-links-item {
    color: $color--basic-white;
    font-weight: 500;
    font-size: 13px;

    &.phone {
      margin-bottom: 8px;
    }

    &.email {
      color: $color--basic-dark-gray;
    }
  }

  .contact-links-item {
    transition: color 0.15s ease;
  }

  .contact-links-item:hover, .category__link:hover {
    color: $color--basic-light-gray;
  }

  .footer__join-us {
    display: none;
  }

  .footer__social {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 24px;
    grid-area: socials;
  }

  .footer__social-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .social-item {
    --icon-size: 13px;
    --circle-size: 32px;

    flex-shrink: 0;
    transition: background-color 0.15s ease;

    &:not(:last-child) {
      margin-right: 16px;
    }

    &:hover {
      --circle-bg-color: #{$color--basic-blue};
    }
  }

  .links__category {
    background-color: $color--basic-menu;
    padding: 16px;
    margin-bottom: 8px;
    grid-column-start: 1;
    grid-column-end: 3;
  }

  .category__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .category__title-text {
    font-weight: 500;
    font-size: 15px;
    color: $color--basic-white;

    &--link {
      cursor: pointer;
    }
  }

  .category__title-shape {
    width: 15px;
    height: 15px;
    position: relative;

    &:after, &:before {
      position: absolute;
      content: '';
      display: block;
      height: 2px;
      width: 100%;
      margin-top: -1px;
      top: 50%;
      z-index: 1;
      border-radius: 10px;
      background-color: $color--basic-white;
      transform-origin: center center;
      transition-duration: .2s;
      transition-timing-function: ease-out;
    }

    &:before {
      transition-property: opacity;
    }

    &:after {
      transition-property: transform;
      transform: rotate(90deg);
    }
  }

  .category__title-shape--active {
    &:before {
      opacity: 0;
    }

    &:after {
      transform: rotate(180deg);
    }
  }

  .category__link {
    display: block;
    color: $color--basic-dark-gray;
    font-size: 13px;
    font-weight: 500;
    padding: 4px 0;
  }

  .category__link-more {
    display: inline-block;
    margin-top: 4px;
    padding: 6px;
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 500;
    color: $color--basic-white;
    border-radius: 4px;
    background-color: $color--basic-dark-gray;
    letter-spacing: 0.05em;

    &:hover {
      background-color: $color--basic-black;
    }
  }

  .footer__bottom {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px 0 38px 0;
    color: $color--basic-dark-gray;
  }

  .bottom__payment-systems {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 14px;
  }

  .bottom__content-line {
    flex-direction: column;
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .payment-item {
    filter: $filter--white-to-dark-gray;
    min-height: 17px;
    width: auto;
    margin: 0 4px 10px;
  }

  .bottom__app-markets {
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 16px;
    height: 100%;
  }

  .bottom__appstore:hover,
  .bottom__googleplay:hover {
    --color: #{$color--basic-black};
    --background: #{$color--basic-white};
  }

  .bottom_text {
    font-size: 13px;
    text-align: center;
    margin-bottom: 20px;
  }

  @media screen and #{$screen-less-than-1024} {
    .footer__body {
      max-height: 580px;

      &:after {
        height: 280px;
        background: linear-gradient(0deg, #171E26 0%, rgba(23, 30, 38, 0) 100%);
      }
    }

    .footer__show-more-button {
      left: 0;
      transform: unset;
      width: 100%;
      justify-content: center;
    }

    .bottom__app-markets--top {
      display: none;
    }

    .bottom__app-markets {
      width: 100%;
      display: flex;
      gap: unset;
      margin-bottom: 24px;
    }

    .bottom__appstore,
    .bottom__googleplay {
      --color: #{$color--basic-black};
      --background: #{$color--basic-white};

      width: calc(50% - 8px);

      &:not(:first-child) {
        margin-left: 16px;
      }
    }

    .footer {
      content-visibility: auto;
      contain-intrinsic-size: 630px;
    }

    .links__category {
      cursor: pointer;
      order: -1;

      &.category-sitemap,
      &.category-company,
      &.category-customer,
      &.category-legal-and-policies {
        order: 1;
      }
    }
  }

  // DESKTOP
  @media screen and #{$screen-more-or-1024} {
    .bottom__content-line {
      flex-direction: row;
    }

    .footer {
      background-color: $color--basic-menu;
      z-index: 110;
      position: relative;
      content-visibility: auto;
      contain-intrinsic-size: 500px;
    }

    .footer__container {
      padding: 40px 28px;
    }

    .footer__main {
      padding: 0;
      grid-template-columns: repeat(6, 1fr);
      grid-row-gap: 32px;
      grid-column-gap: 24px;
      grid-template-areas:
          "contact-links company customer policies socials chat-button";
      margin-top: 0;
    }

    .footer__logo {
      display: block;
      margin-bottom: 24px;
    }

    .footer__logo-img {
      display: inline-block;
    }

    .footer__social {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
    }

    .footer__join-us {
      display: block;
      margin-bottom: 16px;
    }

    .footer__social-wrapper {
      display: flex;
      justify-content: flex-start;
    }

    .social-item {
      --icon-size: 13px;
      --circle-size: 32px;

      &:not(:last-child) {
        margin-right: 12px;
      }
    }

    .category-sitemap .category__links-list {
      grid-column-gap: 20px;
    }

    .links__category {
      padding: 0;
      margin: 0;
      width: 100%;
      grid-column-start: auto;
      grid-column-end: auto;
    }

    .category-company {
      grid-area: company;
    }

    .category-customer {
      grid-area: customer;
    }

    .category-legal-and-policies {
      grid-area: policies;
    }

    .category__links-list {
      padding-top: 0;
    }

    .category__link {
      word-break: break-all;
    }

    .category__title {
      margin-bottom: 12px;
    }

    .category__title-text {
      font-size: 19px;
      max-width: 170px;
    }

    .category__title-shape {
      display: none;
    }

    .category__links-list {
      max-height: none;

      &.active {
        margin: 0;
      }
    }

    .footer__bottom {
      padding: 34px 0 0 0;
    }

    .bottom__payment-systems {
      width: 100%;
      justify-content: flex-start;
    }

    .payment-item {
      height: 24px;
      margin: 0 16px 0 0;
    }

    .bottom_text {
      width: 100%;
      text-align: start;
    }
  }

  @media screen and #{$screen-more-or-1024} and #{$screen-less-than-1920} {
    .footer.has-sidebar {
      padding: 0 var(--padding) 0 $padding-pc-sidebar;

      .footer__container {
        max-width: 100%;
      }
    }
  }

  @media only screen and (min-width: 1300px) {
    .footer__body {
      grid-column-gap: 100px;
    }

    .category-sitemap .category__links-list {
      grid-column-gap: 60px;
    }
  }

  @media screen and #{$screen-more-or-1920} {
    .footer {
      display: grid;
      grid-template-columns: 1fr minmax(auto, $size-container-max-width) 1fr;
    }

    .footer.has-sidebar {
      grid-template-columns: minmax(250px, 1fr) minmax(auto, $size-container-max-width) 1fr;
    }

    .footer__container {
      grid-column: 2;
    }
  }
</style>
